import { useDisclosure } from '@mantine/hooks';
import { ReactNode, useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { showError, showSuccess } from 'src/toasts/toasts';
import { GroupMember } from '../grouping';
import useExpenditures from '../hooks/useExpenditures';
import useIncomes from '../hooks/useIncomes';
import useLifeEvents from '../hooks/useLifeEvents';
import {
  ExpenditureRow,
  IncomeRow,
  LifeEventRow,
  buildExpenditureRows,
  buildIncomeRows,
  buildLifeEventRows,
} from '../rows';
import { Period } from '../utils';
import { CashflowsLayout } from './CashflowsLayout';
import { ExpenditureFormModal } from './ExpenditureFormModal';
import { IncomeFormModal } from './IncomeFormModal';
import { LifeEventFormModal } from './LifeEventFormModal';
import { useSaveExpenditure, useSaveIncome, useSaveLifeEvent } from './useCashflowSaves';
import { memberIndex, toGroupMembers } from './useCashflowMembers';
import { reportSaveFailure } from './reportSaveFailure';

/**
 * An individual client's tab reads only that client's rows, so a shared item shows the member's
 * own share and names them alone under "Applies to". The whole item is on the family tab, which
 * is the only view that reads every member — hence `ownRowsOnly`, which holds a shared item's
 * membership and shared fields read-only here rather than letting a partial view rewrite them.
 */
export function ClientCashflowsRedesign({ modelsSection }: { modelsSection: ReactNode }) {
  const { clientId, client } = useCurrentClient();
  const { isLoading: loadingIncomes, incomesData } = useIncomes();
  const { isLoading: loadingExpenditures, expendituresData } = useExpenditures();
  const { lifeEventsData } = useLifeEvents();
  const { members: familyMembers } = useFamilyMembers();

  const [period, setPeriod] = useState<Period>('monthly');
  const [incomeOpen, income] = useDisclosure(false);
  const [expenditureOpen, expenditure] = useDisclosure(false);
  const [lifeEventOpen, lifeEvent] = useDisclosure(false);
  const [editedIncome, setEditedIncome] = useState<IncomeRow | undefined>();
  const [editedExpenditure, setEditedExpenditure] = useState<ExpenditureRow | undefined>();
  const [editedLifeEvent, setEditedLifeEvent] = useState<LifeEventRow | undefined>();

  const saveIncome = useSaveIncome();
  const saveExpenditure = useSaveExpenditure();
  const saveLifeEvent = useSaveLifeEvent();

  const self: GroupMember = {
    clientId,
    firstName: client?.firstName ?? '',
    lastName: client?.lastName ?? '',
  };
  // A client with no family can still record cashflows — against themselves alone.
  const pickableMembers = familyMembers.length > 0 ? toGroupMembers(familyMembers) : [self];
  const byClientId = memberIndex(
    pickableMembers.some(member => member.clientId === clientId)
      ? pickableMembers
      : [self, ...pickableMembers],
  );

  if (loadingIncomes || loadingExpenditures) {
    return <PageLoader absolute={false} />;
  }

  const save = <T,>(
    persist: (submission: T) => Promise<void>,
    noun: string,
    close: () => void,
    clearEdited: () => void,
  ) => {
    return async (submission: T) => {
      try {
        await persist(submission);
        showSuccess(`${noun} saved successfully`);
        close();
        clearEdited();
      } catch (error) {
        if (reportSaveFailure(error, byClientId, noun)) {
          close();
          clearEdited();
          return;
        }
        showError(`Failed to save ${noun.toLowerCase()}`);
      }
    };
  };

  return (
    <>
      <CashflowsLayout
        period={period}
        onPeriodChange={setPeriod}
        incomeRows={buildIncomeRows(incomesData ?? [], byClientId)}
        expenditureRows={buildExpenditureRows(expendituresData ?? [], byClientId)}
        lifeEventRows={buildLifeEventRows(lifeEventsData ?? [], byClientId)}
        incomeActions={{
          onAdd: () => {
            setEditedIncome(undefined);
            income.open();
          },
          onRowClick: row => {
            setEditedIncome(row);
            income.open();
          },
        }}
        expenditureActions={{
          onAdd: () => {
            setEditedExpenditure(undefined);
            expenditure.open();
          },
          onRowClick: row => {
            setEditedExpenditure(row);
            expenditure.open();
          },
        }}
        lifeEventActions={{
          onAdd: () => {
            setEditedLifeEvent(undefined);
            lifeEvent.open();
          },
          onRowClick: row => {
            setEditedLifeEvent(row);
            lifeEvent.open();
          },
        }}
        modelsSection={modelsSection}
      />

      <IncomeFormModal
        opened={incomeOpen}
        onClose={income.close}
        onSubmit={save(saveIncome, 'Income', income.close, () => setEditedIncome(undefined))}
        members={pickableMembers}
        defaultClientId={clientId}
        initialRow={editedIncome}
        ownRowsOnly
      />
      <ExpenditureFormModal
        opened={expenditureOpen}
        onClose={expenditure.close}
        onSubmit={save(saveExpenditure, 'Expenditure', expenditure.close, () =>
          setEditedExpenditure(undefined),
        )}
        members={pickableMembers}
        defaultClientId={clientId}
        initialRow={editedExpenditure}
        ownRowsOnly
      />
      <LifeEventFormModal
        opened={lifeEventOpen}
        onClose={lifeEvent.close}
        onSubmit={save(saveLifeEvent, 'Life event', lifeEvent.close, () =>
          setEditedLifeEvent(undefined),
        )}
        members={pickableMembers}
        defaultClientId={clientId}
        initialRow={editedLifeEvent}
        ownRowsOnly
      />
    </>
  );
}
